import { useEffect, useRef, useState } from 'react';

const resolveElement = (container) => {
	if (!container) return null;

	return container instanceof Element ? container : container.current;
};

const findScrollElement = (element) => {
	if (!element) return null;

	const bottomSheetElement = element.closest?.('[data-rsbs-scroll]');
	if (bottomSheetElement) return bottomSheetElement;

	let parent = element;
	while (parent && parent !== document.body && parent !== document.documentElement) {
		const { overflowY } = window.getComputedStyle(parent);
		const isScrollable = /(auto|scroll|overlay)/.test(overflowY) && parent.scrollHeight > parent.clientHeight;

		if (isScrollable) return parent;

		parent = parent.parentElement;
	}

	return element;
};

const useNearBottom = (threshold = 200, container = null) => {
	const [isNearBottom, setIsNearBottom] = useState(false);
	const prevRef = useRef(false);
	const lastTrackRef = useRef(null);

	const handleScroll = () => {
		const element = findScrollElement(resolveElement(container));

		if (element) {
			const scrollPosition = element.scrollTop;
			const scrollHeight = element.scrollHeight;
			const distanceFromBottom = scrollHeight - scrollPosition - element.clientHeight;

			if (distanceFromBottom >= threshold) {
				if (!prevRef.current) return;

				prevRef.current = false;
				setIsNearBottom(false);
				return;
			}

			const lastTrack = lastTrackRef.current;

			if (lastTrack && scrollHeight < lastTrack.scrollHeight) {
				lastTrackRef.current = null;
			}

			const track = lastTrackRef.current;

			if (track && scrollPosition <= track.scrollTop) return;

			lastTrackRef.current = { scrollTop: scrollPosition, scrollHeight };

			if (prevRef.current) return;

			prevRef.current = true;
			setIsNearBottom(true);
			return;
		}

		const scrollTop = window.scrollY || document.documentElement.scrollTop;
		const windowHeight = window.innerHeight;
		const documentHeight = document.documentElement.scrollHeight;

		const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
		const isScrollable = documentHeight > windowHeight + threshold;

		if (!isScrollable) return;

		const next = distanceFromBottom < threshold;

		if (prevRef.current !== next) {
			prevRef.current = next;
			setIsNearBottom(next);
		}
	};

	const scrollToTop = () => {
		const element = findScrollElement(resolveElement(container));

		if (element) {
			element.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
			return;
		}

		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	};

	useEffect(() => {
		const element = findScrollElement(resolveElement(container));
		const target = element ?? window;

		if (element) lastTrackRef.current = null;

		target.addEventListener('scroll', handleScroll, { passive: true });

		handleScroll();

		return () => {
			target.removeEventListener('scroll', handleScroll);
		};
	}, [container, threshold]);

	return {
		isNearBottom,
		scrollToTop,
	};
};

export default useNearBottom;
